import { useCallback, useEffect, useState } from 'react';
import { useShellAuth0 } from '@opengov/shell-auth0-react';
import { useInterval } from 'use-interval';
import { sessionActionCreators } from '@opengov/app-core/session/actionsAndReducer';
import { getSessionQuery } from '@opengov/bnp-core';
import { ExternalRoutes } from '../utils/routes';
import { ISession } from '@opengov/app-core/types';
import { queryClient } from '../apiHooks/core/queryClient';
import { getSessionV3QueryOptions } from '../apiHooks/session/useGetSessionV3';
import { accessTokenForNonReactContextsStore } from '@opengov/app-core/auth/accessTokenForNonReactContextsStore';

export const CHECK_SESSION_INTERVAL = 60 * 60 * 1000; // Hourly

export function useCheckSession() {
  const { getAccessTokenSilently, isAuthenticated: isAuth0Authenticated, isLoading: isAuth0Loading } = useShellAuth0();
  const [isSessionCheckComplete, setIsSessionCheckComplete] = useState(false);
  const [checkInProgress, setCheckInProgress] = useState(false);

  useEffect(() => {
    accessTokenForNonReactContextsStore.setState({ getAccessTokenSilently: () => getAccessTokenSilently() });
  }, [getAccessTokenSilently]);

  const checkSession = useCallback(async () => {
    // Don't check while Auth0 is still loading
    if (isAuth0Loading || checkInProgress) {
      return;
    }

    setCheckInProgress(true);

    try {
      // Check auth0
      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      accessTokenForNonReactContextsStore.setState({ accessToken: token });

      // Exchange Auth0 token for Delphi session cookie
      const queryOptions = getSessionV3QueryOptions(getAccessTokenSilently);
      await queryClient.fetchQuery(queryOptions);

      // Populate legacy v1 session in compose-app for backwards compatibility
      sessionActionCreators.fetchSession();
      const v1Session = await queryClient.fetchQuery(getSessionQuery());
      sessionActionCreators.fetchSessionSuccess(v1Session as unknown as ISession);
    } catch (error) {
      console.error('Session check failed. Redirecting to login page.', error);
      window.location.href = ExternalRoutes.login();
    } finally {
      setCheckInProgress(false);
      setIsSessionCheckComplete(true);
    }
  }, [getAccessTokenSilently, isAuth0Loading, isAuth0Authenticated]);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  useInterval(() => void checkSession(), CHECK_SESSION_INTERVAL, false);

  return {
    getAccessTokenSilently,
    isAuthenticated: isAuth0Authenticated && isSessionCheckComplete,
    isLoading: isAuth0Loading || checkInProgress
  };
}
